import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector, ReversibleEffector } from "./EffectorBase";
import { CustomEventTypes, EasingFunctionTypes, PropertyTypes, SaveModificationTypes } from "../../../globals";
import Vector3Property from "../../../components/properties/Vector3Property";
import Engine from '../../Engine';
import ProjectSaver from '../../ProjectSaver';
import MathUtils from '../../utilities/MathUtils';
import { TimelineFragment } from '../TimelineFragment';
import { areArraysEqual } from '../../utilities/Misc';

export interface EffectorTransformScaleSaveData
{
	scale: { x: number, y: number, z: number };
	easing: EasingFunctionTypes;
	reversed: boolean;
	loopCount: number;
}

export default class EffectorTransformScale extends EffectorBase<EffectorTransformScale> implements LoopableEffector, ReversibleEffector
{
	scale: THREE.Vector3;
	deltaScale: THREE.Vector3;
	easing: EasingFunctionTypes;
	reversed: boolean;
	loopCount: number;
	defaultValues: { scale: { x: number, y: number, z: number } };

	constructor(fragment : TimelineFragment)
	{
		super(fragment);

		this.scale = new THREE.Vector3(0, 0, 0);
		this.deltaScale = new THREE.Vector3();
		this.easing = EasingFunctionTypes.Linear;
		this.reversed = false;
		this.loopCount = 1;
		this.defaultValues = {
			scale: { x: 0, y: 0, z: 0 },
		};
	}

	save = () : EffectorTransformScaleSaveData =>
	{
		const output = {
			scale: { x: this.scale.x, y: this.scale.y, z: this.scale.z },
			easing: this.easing,
			reversed: this.reversed,
			loopCount: this.loopCount,
		};

		return output;
	};

	load = (savedData: EffectorTransformScaleSaveData) =>
	{
		if ("valueAtStart" in savedData && "valueAtEnd" in savedData) //old format
		{
			const start = savedData.valueAtEnd as any;
			const end = savedData.valueAtEnd as any;
			this.scale.set(end.x - start.x, end.y - start.y, end.z - start.z);
		}
		else if (savedData.scale)
		{
			this.scale.set(savedData.scale.x, savedData.scale.y, savedData.scale.z);
		}

		if (savedData.easing)
		{
			this.easing = savedData.easing.charAt(0).toLowerCase() + savedData.easing.slice(1) as EasingFunctionTypes;
		}
		if (savedData.reversed) this.reversed = savedData.reversed;
		if (savedData.loopCount) this.loopCount = savedData.loopCount;

	};

	copyValues = (sourceEffector: EffectorTransformScale) =>
	{
		this.scale.copy(sourceEffector.scale);
		this.easing = sourceEffector.easing;
		this.reversed = sourceEffector.reversed;
		this.loopCount = sourceEffector.loopCount;
	}

	setScale = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.scale.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.scale.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.scale.z = z;
	}

	resetState = () =>
	{
		if (!this.fragment.previousTime) return;

		this.forEachTargetObject((targetObject) =>
		{
			targetObject.scale.sub(this.deltaScale);
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectScaleChanged);
		}
	}

	update = (fragmentTime: number) =>
	{
		if (!fragmentTime) return;

		this.deltaScale.copy(this.scale).multiplyScalar(fragmentTime);
		MathUtils.round(this.deltaScale);

		this.forEachTargetObject((targetObject) => 
		{
			targetObject.scale.add(this.deltaScale);
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectScaleChanged);
		}
	}

	onAdded = () =>
	{
		for (const object of this.targetObjects())
		{
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Scale);
		}
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Scale",
				PropertyTypes.Vector3,
				<Vector3Property
					value={[this.scale.x, this.scale.y, this.scale.z]}
					step={0.1}
					renderChildrenInSeparateRows={true}
					defaultValue={this.defaultValues.scale}
					decimals={4}
				/>,
				() => {return [this.scale.x, this.scale.y, this.scale.z];},
				(v) => {this.setScale(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
		];
	}
}
